import { ICall, ICallParticipant } from '@app-types/websocket/calls.types';

export const NEW_CALL_MAX_AGE_MS = 1000 * 60;

/**
 * The states in which a call is still going on. Everything else - FAILED, ENDED,
 * UNKNOWN - is terminal and is set by the backend when it decides a call is over,
 * which is how a ringing call is stopped on every device at once.
 */
export function isCallActive(call?: ICall): boolean {
  return !!call
    && (call.callStatus === 'CREATE' || call.callStatus === 'CREATED' || call.callStatus === 'STARTED');
}

export function getParticipant(call: ICall | undefined, userId: number): ICallParticipant | undefined {
  if (!call || !Array.isArray(call.participants)) {
    return undefined;
  }
  return call.participants.find((participant) => !!participant && participant.participant === userId);
}

/** A participant is out of the call for good exactly when the backend set an exit time. */
export function hasExited(participant?: ICallParticipant): boolean {
  return !!participant && participant.cleanExitTime !== undefined && participant.cleanExitTime > 0;
}

/** Still ringing or still connected, i.e. this participant is holding the call open. */
export function isPresent(participant?: ICallParticipant): boolean {
  return !!participant
    && !hasExited(participant)
    && (participant.responseStatus === 'AWAITING' || participant.responseStatus === 'ANSWER');
}

/** Answered and has not left. */
export function isConnected(participant?: ICallParticipant): boolean {
  return !!participant && !hasExited(participant) && participant.responseStatus === 'ANSWER';
}

export function countPresent(call: ICall): number {
  const participants = Array.isArray(call.participants) ? call.participants : [];
  return participants.filter(isPresent).length;
}

export function countConnectedOthers(call: ICall, userId: number): number {
  const participants = Array.isArray(call.participants) ? call.participants : [];
  return participants.filter((participant) => isConnected(participant) && participant.participant !== userId).length;
}

/**
 * A call that this device should be showing: one that is still going on, that still has
 * somebody to talk to, and that the user has not already left.
 */
export function isCallJoinable(call: ICall, userId: number): boolean {
  return isCallActive(call)
    && countPresent(call) > 1
    && isPresent(getParticipant(call, userId));
}

/**
 * How far back a call can be and still matter to this device. The backend ends calls long
 * before this; the bound only stops a row that escaped cleanup from looking current, so it
 * has to stay above the longest call the backend will allow.
 */
export const MAX_CALL_LIFETIME_MS = 1000 * 60 * 60 * 5;

/** True while this user is connected to a call that is still going on. */
export function isUserInCall(call: ICall, userId: number, now: number = Date.now()): boolean {
  return isCallActive(call)
    && call.start > now - MAX_CALL_LIFETIME_MS
    && isConnected(getParticipant(call, userId));
}

/**
 * How long after pressing "call" a new call still counts as the one this device placed.
 * Matches the answer window, so a stale request can never claim a later call.
 */
export const OUTGOING_CALL_MATCH_MS = 1000 * 60;

/**
 * True when this device is the one the user pressed "call" on for this call.
 *
 * The same account can be signed in on a phone and a computer at once, and the backend
 * tells both about every call because the participant row belongs to the account, not to
 * a device. The call request is remembered per device, so matching it against the call is
 * what keeps the other device from opening its camera for a call it is not making.
 */
export function wasCallPlacedOnThisDevice(
  call: ICall,
  userId: number,
  requestedChatId: number,
  requestedAt: number,
  now: number = Date.now()
): boolean {
  return call.initiator === userId
    && requestedChatId > 0
    && call.chatId === requestedChatId
    && call.start >= requestedAt - OUTGOING_CALL_MATCH_MS
    && now - requestedAt < OUTGOING_CALL_MATCH_MS;
}

/**
 * New calls expire from the call UI after the answer window, but an already
 * connected call must remain eligible until a participant actually hangs up.
 */
export function isCallEligibleForDisplay(
  call: ICall,
  activeCallId: number | undefined,
  isInCall: boolean,
  now: number = Date.now()
): boolean {
  if (!isCallActive(call)) {
    return false;
  }

  const isWithinAnswerWindow = call.start > now - NEW_CALL_MAX_AGE_MS;
  const isConnectedCall = isInCall && call.id !== undefined && call.id === activeCallId;

  return isWithinAnswerWindow || isConnectedCall;
}



// WEBPACK FOOTER //
// ./src/services/calls/calls.utils.ts